import React, { useMemo } from 'react';
import { navyNight, navyDarkPanel, neutralWhite, blue400 } from '../themes/colors';
import { Tooltip } from '../tooltip';
import {
	ThemeCardRoot,
	ThemeCardHeader,
	ThemeCardLeft,
	RadioButton,
	ThemeName,
	ThemeLabel,
	ThemeLinkLabel,
	ThemePreview,
	WidgetPreview,
	NumberValue,
	ChartPreview,
	MiniChart,
} from './ThemeCard.style';

export type ThemeCardTheme = {
	id: string;
	name: string;
	text_color?: string | null;
	background_color?: string | null;
	background_image?: string | null;
	background_image_thumbnail?: string | null;
	background_position?: string | null;
	widget_background_color?: string | null;
	widget_chart_color?: string | null;
	is_custom?: boolean;
};

export type ThemeCardProps = {
	theme: ThemeCardTheme;
	selected?: boolean;
	onSelect?: (themeId: string) => void;

	canEditThemes?: boolean;
	editHref?: string;

	defaultLabel: string;
	editLabel: string;
	upgradeEditTooltip: string;

	numberValueLabel?: string;
	yAxisTopLabel?: string;
	yAxisBottomLabel?: string;
};

const defaultGenerateChartPath = (): string => {
	const points = 20;
	const width = 280;
	const height = 60;
	const amplitude = 15;
	const baseY = height / 2;

	let path = `M 0 ${baseY}`;
	for (let i = 0; i <= points; i++) {
		const x = (i / points) * width;
		const y = baseY + Math.sin((i / points) * Math.PI * 2) * amplitude;
		path += ` L ${x} ${y}`;
	}
	return path;
};

export const ThemeCard: React.FC<ThemeCardProps> = ({
	theme,
	selected = false,
	onSelect,
	canEditThemes = false,
	editHref,

	defaultLabel,
	editLabel,
	upgradeEditTooltip,

	numberValueLabel = '+25%',
	yAxisTopLabel,
	yAxisBottomLabel,
}) => {
	const isDefault = !theme.is_custom;

	const backgroundColor = theme.background_color || navyNight;
	const backgroundImage = theme.background_image_thumbnail || theme.background_image || null;
	const backgroundSize = theme.background_position || 'cover';
	const widgetBgColor = theme.widget_background_color || navyDarkPanel;
	const textColor = theme.text_color || neutralWhite;
	const chartColor = theme.widget_chart_color || blue400;

	const gradientId = useMemo(() => `gradient-${theme.id}`, [theme.id]);
	const chartPath = useMemo(() => defaultGenerateChartPath(), []);

	return (
		<ThemeCardRoot
			role="button"
			tabIndex={0}
			selected={selected}
			onClick={() => onSelect?.(theme.id)}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onSelect?.(theme.id);
				}
			}}
		>
			<ThemeCardHeader>
				<ThemeCardLeft>
					<RadioButton selected={selected} />
					<ThemeName>{theme.name}</ThemeName>
				</ThemeCardLeft>

				{isDefault ? (
					<ThemeLabel isDefault>{defaultLabel}</ThemeLabel>
				) : canEditThemes ? (
					editHref ? (
						<ThemeLinkLabel
							href={editHref}
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.stopPropagation();
								}
							}}
						>
							{editLabel}
						</ThemeLinkLabel>
					) : (
						<ThemeLabel
							isDefault={false}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							{editLabel}
						</ThemeLabel>
					)
				) : (
					<Tooltip message={upgradeEditTooltip} placement="bottom">
						<ThemeLabel
							isDefault={false}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							{editLabel}
						</ThemeLabel>
					</Tooltip>
				)}
			</ThemeCardHeader>

			<ThemePreview
				backgroundColor={backgroundColor}
				backgroundImage={backgroundImage}
				backgroundSize={backgroundSize}
			>
				<WidgetPreview widgetBackgroundColor={widgetBgColor} textColor={textColor}>
					{numberValueLabel ? <NumberValue color={chartColor}>{numberValueLabel}</NumberValue> : null}
				</WidgetPreview>

				<ChartPreview widgetBackgroundColor={widgetBgColor}>
					<MiniChart viewBox="-20 0 300 60" preserveAspectRatio="none">
						<defs>
							<linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
								<stop offset="0%" stopColor={chartColor} stopOpacity="0.5" />
								<stop offset="100%" stopColor={chartColor} stopOpacity="0.1" />
							</linearGradient>
						</defs>

						<path d={chartPath} fill="none" stroke={chartColor} strokeWidth="2" />
						<path d={`${chartPath} L 280 60 L 0 60 Z`} fill={`url(#${gradientId})`} />

						{yAxisTopLabel ? (
							<text x="-10" y="10" fill={textColor} fontSize="10" opacity="0.7">
								{yAxisTopLabel}
							</text>
						) : null}

						{yAxisBottomLabel ? (
							<text x="-10" y="58" fill={textColor} fontSize="10" opacity="0.7">
								{yAxisBottomLabel}
							</text>
						) : null}
					</MiniChart>
				</ChartPreview>
			</ThemePreview>
		</ThemeCardRoot>
	);
};
